import React, { useState, useEffect } from "react";
import ReactModal from "react-modal";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import ReactMarkdown from "react-markdown";
import Swal from "sweetalert2";
import moment from "moment";
import {
  FiEdit3,
  FiTrash2,
  FiChevronLeft,
  FiChevronRight
} from "react-icons/fi";
import { FaCirclePlus } from "react-icons/fa6";
import Container from "../../components/Container";
import { StyledTooltip } from "../../components/Tooltip";
import { BrComerceApiService } from "../../../../services/BrComerceApiService";
import { Loading } from "../../../../components/Loading";
import DatePicker from "../../../../components/Input/DatePicker";
import Select from "react-select";
import { selectStyle } from "../../../../components/InputSelectAsync";
import styles from "./styles.module.scss";

const api = new BrComerceApiService();

const AnnouncementSchema = Yup.object().shape({
  title: Yup.string()
    .max(150, "Máximo de 150 caracteres")
    .required("Obrigatório"),
  content: Yup.string().required("Obrigatório"),
  target: Yup.string()
    .oneOf(["Todos", "Administradores", "Usuários", "Gerentes"])
    .required("Obrigatório"),
  severity: Yup.string()
    .oneOf(["Informação", "Aviso", "Crítico"])
    .required("Obrigatório"),
  starts_at: Yup.date().nullable(),
  ends_at: Yup.date().nullable(),
  active: Yup.boolean()
});

const targetOptions = [
  { value: "Todos", label: "Todos" },
  { value: "Administradores", label: "Administradores" },
  { value: "Usuários", label: "Usuários" },
  { value: "Gerentes", label: "Gerentes" }
];

const severityOptions = [
  { value: "Informação", label: "Informação" },
  { value: "Aviso", label: "Aviso" },
  { value: "Crítico", label: "Crítico" }
];

const Announcements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [readsModalOpen, setReadsModalOpen] = useState(false);
  const [readsData, setReadsData] = useState([]);
  const [searchRead, setSearchRead] = useState("");
  const [selectedAnnouncementName, setSelectedAnnouncementName] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const response = await api.makeHttpRequest({
        method: "GET",
        url: "/admin/announcements"
      });
      setAnnouncements(response || []);
    } catch (error) {
      Swal.fire("Erro", "Falha ao buscar os anúncios", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const handleOpenModal = (announcement = null) => {
    setEditingAnnouncement(announcement);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setEditingAnnouncement(null);
    setModalOpen(false);
  };

  const handleDelete = async id => {
    const result = await Swal.fire({
      title: "Tem certeza?",
      text: "Isso irá remover o anúncio para todos os usuários.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Sim, remover!"
    });

    if (result.value) {
      try {
        setLoading(true);
        await api.makeHttpRequest({
          method: "DELETE",
          url: `/admin/announcements/${id}`
        });
        Swal.fire("Removido!", "Anúncio deletado com sucesso.", "success");
        fetchAnnouncements();
      } catch (error) {
        Swal.fire("Erro", "Não foi possível remover o anúncio", "error");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setLoading(true);
      if (editingAnnouncement) {
        await api.makeHttpRequest({
          method: "PUT",
          url: `/admin/announcements/${editingAnnouncement.id}`,
          data: values
        });
        Swal.fire("Sucesso", "Anúncio atualizado com sucesso", "success");
      } else {
        await api.makeHttpRequest({
          method: "POST",
          url: "/admin/announcements",
          data: values
        });
        Swal.fire("Sucesso", "Anúncio criado com sucesso", "success");
      }
      handleCloseModal();
      fetchAnnouncements();
    } catch (error) {
      Swal.fire("Erro", "Falha ao salvar o anúncio", "error");
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleViewStats = async (id, title) => {
    setCurrentPage(1);
    setSelectedAnnouncementName(title);
    setSearchRead("");

    try {
      setLoading(true);

      const response = await api.makeHttpRequest({
        method: "GET",
        url: `/admin/announcements/${id}/stats`
      });

      setReadsData(response || []);

      setReadsModalOpen(true);
    } catch (error) {
      console.error(error);
      Swal.fire("Erro", "Não foi possível carregar as estatísticas.", "error");
    } finally {
      setLoading(false);
    }
  };

  const filteredReads = readsData.filter(read => {
    const searchTarget = (
      read.user_name ||
      read.user_email ||
      ""
    ).toLowerCase();
    return searchTarget.includes(searchRead.toLowerCase());
  });

  const totalPages = Math.ceil(filteredReads.length / itemsPerPage);

  const currentReads = filteredReads.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  return (
    <>
      <Loading loading={loading} />
      <Container title="Gestão de Anúncios">
        <div className={styles.contentBody}>
          <div className={styles.filters}>
            <div style={{ flex: 1 }}></div>

            <div className={styles.containerBtnAddUser}>
              <button
                type="button"
                className={styles.btnAddUser}
                onClick={() => handleOpenModal()}
              >
                <FaCirclePlus fill="#fff" color="#fff" />
                <span>Novo Anúncio</span>
              </button>
            </div>
          </div>

          <div className={styles.contentTable}>
            <table className={styles.tableContainer}>
              <thead>
                <tr>
                  <th width="15%">Título</th>
                  <th width="12%">Público</th>
                  <th width="12%">Severidade</th>
                  <th width="12%">Início</th>
                  <th width="12%">Fim</th>
                  <th width="12%">Status</th>
                  <th width="15%">Leituras</th>
                  <th width="10%">Ações</th>
                </tr>
              </thead>
              <tbody>
                {announcements.map(item => (
                  <tr key={item.id}>
                    <td>{item.title}</td>
                    <td>{item.target}</td>
                    <td>{item.severity}</td>
                    <td>
                      {item.starts_at
                        ? moment.utc(item.starts_at).format("DD/MM/YYYY")
                        : "--"}
                    </td>
                    <td>
                      {item.ends_at
                        ? moment.utc(item.ends_at).format("DD/MM/YYYY")
                        : "Indefinido"}
                    </td>
                    <td>
                      <span
                        className={
                          item.active
                            ? styles.badgeActive
                            : styles.badgeInactive
                        }
                      >
                        {item.active ? "Ativo" : "Inativo"}
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        onClick={() => handleViewStats(item.id, item.title)}
                        className={styles.statsButton}
                      >
                        Ver Estatísticas
                      </button>
                    </td>
                    <td className={styles.tooltipWrapper}>
                      <StyledTooltip text="Editar" position="top" arrow>
                        <div>
                          <FiEdit3
                            onClick={() => handleOpenModal(item)}
                            style={{ cursor: "pointer", marginRight: "10px" }}
                            size={20}
                            color="#409E6A"
                          />
                        </div>
                      </StyledTooltip>
                      <StyledTooltip text="Deletar" position="top" arrow>
                        <div>
                          <FiTrash2
                            onClick={() => handleDelete(item.id)}
                            style={{ cursor: "pointer" }}
                            size={20}
                            color="#409E6A"
                          />
                        </div>
                      </StyledTooltip>
                    </td>
                  </tr>
                ))}
                {announcements.length === 0 && (
                  <tr>
                    <td
                      colSpan="8"
                      style={{ textAlign: "center", padding: "30px" }}
                    >
                      Nenhum anúncio encontrado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Container>

      <ReactModal
        isOpen={modalOpen}
        onRequestClose={handleCloseModal}
        className={styles.modalContent}
        overlayClassName={styles.modalOverlay}
      >
        <h2>{editingAnnouncement ? "Editar Anúncio" : "Novo Anúncio"}</h2>

        <Formik
          initialValues={{
            title: editingAnnouncement?.title || "",
            content: editingAnnouncement?.content || "",
            target: editingAnnouncement?.target || "Todos",
            severity: editingAnnouncement?.severity || "Informação",
            starts_at: editingAnnouncement?.starts_at
              ? moment.utc(editingAnnouncement.starts_at).format("YYYY-MM-DD")
              : "",
            ends_at: editingAnnouncement?.ends_at
              ? moment.utc(editingAnnouncement.ends_at).format("YYYY-MM-DD")
              : "",
            active:
              editingAnnouncement !== null ? editingAnnouncement.active : true
          }}
          validationSchema={AnnouncementSchema}
          onSubmit={(values, helpers) => {
            const payload = {
              ...values,
              starts_at: values.starts_at ? values.starts_at : null,
              ends_at: values.ends_at ? values.ends_at : null
            };
            handleSubmit(payload, helpers);
          }}
        >
          {({ values, isSubmitting, setFieldValue }) => (
            <Form className={styles.formContainer}>
              <div className={styles.fieldsRow}>
                <div className={styles.fieldGroup}>
                  <label>Título (Máx 150 chars)</label>
                  <div className={styles.inputBrcom}>
                    <Field
                      name="title"
                      type="text"
                      placeholder="Digite o título do anúncio"
                    />
                  </div>
                  <ErrorMessage
                    name="title"
                    component="div"
                    className={styles.error}
                  />
                </div>

                <div
                  className={styles.fieldGroup}
                  style={{ flex: "0 1 auto", justifyContent: "center" }}
                >
                  <label>Ativo?</label>
                  <label className={styles.checkboxContainer}>
                    <Field name="active" type="checkbox" />
                    <span className={styles.checkmark}></span>
                  </label>
                </div>
              </div>

              <div className={styles.fieldsRow}>
                <div className={styles.fieldGroup}>
                  <label>Público-Alvo</label>
                  <Select
                    options={targetOptions}
                    value={targetOptions.find(
                      option => option.value === values.target
                    )}
                    onChange={selectedOption =>
                      setFieldValue("target", selectedOption.value)
                    }
                    styles={selectStyle}
                    placeholder="Selecione o público"
                    isSearchable={false}
                  />
                </div>

                <div className={styles.fieldGroup}>
                  <label>Severidade</label>
                  <Select
                    options={severityOptions}
                    value={severityOptions.find(
                      option => option.value === values.severity
                    )}
                    onChange={selectedOption =>
                      setFieldValue("severity", selectedOption.value)
                    }
                    styles={selectStyle}
                    placeholder="Selecione a severidade"
                    isSearchable={false}
                  />
                </div>
              </div>

              <div className={styles.fieldsRow}>
                <div className={styles.fieldGroup}>
                  <label>Período de Exibição (Opcional)</label>
                  <div className={styles.inputBrcomRelative}>
                    <DatePicker
                      className="datePickerConfig"
                      placeholder="Selecione início e fim"
                      dateStart={
                        values.starts_at
                          ? moment(values.starts_at).format("DD/MM/YYYY")
                          : null
                      }
                      dateFinal={
                        values.ends_at
                          ? moment(values.ends_at).format("DD/MM/YYYY")
                          : null
                      }
                      showClearButton={!!values.starts_at || !!values.ends_at}
                      onClearFields={() => {
                        setFieldValue("starts_at", "");
                        setFieldValue("ends_at", "");
                      }}
                      change={({ dateStart, dateEnd }) => {
                        setFieldValue(
                          "starts_at",
                          dateStart
                            ? moment(dateStart, "DD/MM/YYYY").format(
                                "YYYY-MM-DD"
                              )
                            : ""
                        );
                        setFieldValue(
                          "ends_at",
                          dateEnd
                            ? moment(dateEnd, "DD/MM/YYYY").format("YYYY-MM-DD")
                            : ""
                        );
                      }}
                      range
                    />
                  </div>
                </div>
              </div>

              <div className={styles.markdownArea}>
                <div className={styles.editor}>
                  <label>Conteúdo (Markdown)</label>
                  <Field
                    name="content"
                    as="textarea"
                    rows="10"
                    placeholder="Escreva o conteúdo do anúncio aqui..."
                  />
                  <ErrorMessage
                    name="content"
                    component="div"
                    className={styles.error}
                  />
                </div>
                <div className={styles.preview}>
                  <label>Pré-visualização</label>
                  <div className={styles.previewBox}>
                    <ReactMarkdown>{values.content}</ReactMarkdown>
                  </div>
                </div>
              </div>

              <div className={styles.buttonsContainer}>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className={styles.cancelButton}
                  disabled={isSubmitting}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={styles.saveButton}
                  style={{ cursor: isSubmitting ? "wait" : "pointer" }}
                >
                  {isSubmitting ? "Salvando..." : "Salvar Anúncio"}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </ReactModal>
      {/* MODAL DE ESTATÍSTICAS DE LEITURA */}
      <ReactModal
        isOpen={readsModalOpen}
        onRequestClose={() => setReadsModalOpen(false)}
        className={styles.modalContent}
        overlayClassName={styles.modalOverlay}
      >
        <h2>
          Leituras:{" "}
          <span style={{ color: "#409e6a" }}>{selectedAnnouncementName}</span>
        </h2>

        <div className={styles.formContainer}>
          <div className={styles.fieldsRow}>
            <div className={styles.inputBrcom} style={{ maxWidth: "400px" }}>
              <input
                type="text"
                placeholder="Buscar por nome do usuário..."
                value={searchRead}
                onChange={e => {
                  setSearchRead(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                fontWeight: "600",
                color: "#666"
              }}
            >
              Total de leituras: {readsData.length}
            </div>
          </div>

          {/* Tabela de Leitores */}
          <div
            style={{
              maxHeight: "400px",
              overflowY: "auto",
              marginTop: "10px",
              overflowX: "hidden"
            }}
          >
            <table
              className={styles.tableContainer}
              style={{ width: "100%", tableLayout: "fixed", minWidth: "unset" }}
            >
              <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                <tr>
                  <th style={{ width: "45%" }}>Nome</th>
                  <th style={{ width: "25%" }}>Perfil</th>
                  <th style={{ width: "30%" }}>Lido em</th>
                </tr>
              </thead>
              <tbody>
                {currentReads.map((read, index) => (
                  <tr key={index}>
                    <td style={{ wordBreak: "break-word" }}>
                      {read.user_name ||
                        read.user_email ||
                        "Usuário Desconhecido"}
                    </td>
                    <td style={{ textTransform: "capitalize" }}>
                      {read.user_profile || "--"}
                    </td>
                    <td>
                      {read.read_at
                        ? moment(read.read_at)
                            .subtract(3, "hours")
                            .format("DD/MM/YYYY [às] HH:mm")
                        : "--"}
                    </td>
                  </tr>
                ))}
                {filteredReads.length === 0 && (
                  <tr>
                    <td
                      colSpan="3"
                      style={{ textAlign: "center", padding: "30px" }}
                    >
                      {searchRead
                        ? "Nenhum usuário encontrado na busca."
                        : "Nenhuma leitura registrada ainda."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "20px",
                marginTop: "15px",
                fontSize: "14px",
                color: "#666"
              }}
            >
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => prev - 1)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "32px",
                  height: "32px",
                  borderRadius: "6px",
                  border: "1px solid #ccc",
                  background: currentPage === 1 ? "#f5f5f5" : "#fff",
                  color: currentPage === 1 ? "#aaa" : "#409e6a",
                  cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  transition: "all 0.2s"
                }}
              >
                <FiChevronLeft size={20} />
              </button>

              <span style={{ fontWeight: "600" }}>
                Página {currentPage} de {totalPages}
              </span>

              <button
                type="button"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => prev + 1)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "32px",
                  height: "32px",
                  borderRadius: "6px",
                  border: "1px solid #ccc",
                  background: currentPage === totalPages ? "#f5f5f5" : "#fff",
                  color: currentPage === totalPages ? "#aaa" : "#409e6a",
                  cursor:
                    currentPage === totalPages ? "not-allowed" : "pointer",
                  transition: "all 0.2s"
                }}
              >
                <FiChevronRight size={20} />
              </button>
            </div>
          )}
          <div className={styles.buttonsContainer}>
            <button
              type="button"
              onClick={() => setReadsModalOpen(false)}
              className={styles.saveButton}
            >
              Fechar
            </button>
          </div>
        </div>
      </ReactModal>
    </>
  );
};

export { Announcements };
